import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, CheckCircle2, AlertTriangle, Info } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/portal/admin/security-alerts")({
  head: () => ({
    meta: [
      { title: "Security alerts | Nkadimeng Danny Legal & Compliance" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: SecurityAlertsPage,
});

type Alert = {
  id: string;
  event_type: string;
  severity: "info" | "warning" | "critical";
  email: string | null;
  ip: string | null;
  user_agent: string | null;
  metadata: Record<string, unknown> | null;
  notified_admins: boolean;
  reviewed_at: string | null;
  created_at: string;
};

function SecurityAlertsPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<"unreviewed" | "all">("unreviewed");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-security-alerts", filter],
    queryFn: async () => {
      let q = supabase
        .from("security_alerts")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(200);
      if (filter === "unreviewed") q = q.is("reviewed_at", null);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Alert[];
    },
  });

  const stats = useMemo(() => {
    const rows = data ?? [];
    return {
      total: rows.length,
      critical: rows.filter((r) => r.severity === "critical").length,
      warning: rows.filter((r) => r.severity === "warning").length,
      info: rows.filter((r) => r.severity === "info").length,
    };
  }, [data]);

  async function markReviewed(id: string) {
    const { data: userRes } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("security_alerts")
      .update({ reviewed_at: new Date().toISOString(), reviewed_by: userRes.user?.id ?? null })
      .eq("id", id);
    if (error) {
      toast.error("Couldn't mark reviewed", { description: error.message });
      return;
    }
    toast.success("Marked reviewed");
    qc.invalidateQueries({ queryKey: ["admin-security-alerts"] });
  }

  return (
    <div className="space-y-6">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Admin console</p>
        <h2 className="mt-1 flex items-center gap-2 font-display text-2xl text-navy">
          <ShieldCheck className="h-5 w-5" /> Security alerts
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Suspicious authentication activity — failed admin sign-ins, admin
          sign-in successes, password changes and client sign-in bursts. High
          signal events also email the practice owner.
        </p>
      </header>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Showing" value={stats.total} />
        <StatCard label="Critical" value={stats.critical} tone="critical" />
        <StatCard label="Warning" value={stats.warning} tone="warning" />
        <StatCard label="Info" value={stats.info} tone="info" />
      </div>

      <div className="flex gap-2">
        <Button
          size="sm"
          variant={filter === "unreviewed" ? "default" : "outline"}
          onClick={() => setFilter("unreviewed")}
        >
          Unreviewed
        </Button>
        <Button
          size="sm"
          variant={filter === "all" ? "default" : "outline"}
          onClick={() => setFilter("all")}
        >
          All (last 200)
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading alerts…
        </div>
      ) : (data?.length ?? 0) === 0 ? (
        <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
          No alerts to review. Nothing suspicious has been detected.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="p-3">When</th>
                <th className="p-3">Event</th>
                <th className="p-3">Account</th>
                <th className="p-3">IP</th>
                <th className="p-3">Device</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {data!.map((a) => (
                <tr key={a.id} className="border-t border-border align-top">
                  <td className="p-3 whitespace-nowrap text-xs text-muted-foreground">
                    {new Date(a.created_at).toLocaleString("en-ZA", {
                      timeZone: "Africa/Johannesburg",
                    })}
                  </td>
                  <td className="p-3">
                    <SeverityBadge severity={a.severity} />
                    <div className="mt-1 font-medium text-navy">{a.event_type}</div>
                    {a.metadata && Object.keys(a.metadata).length > 0 && (
                      <pre className="mt-1 whitespace-pre-wrap text-[11px] text-muted-foreground">
                        {JSON.stringify(a.metadata, null, 0)}
                      </pre>
                    )}
                  </td>
                  <td className="p-3 text-xs">{a.email ?? "—"}</td>
                  <td className="p-3 text-xs">{a.ip ?? "—"}</td>
                  <td className="p-3 text-[11px] text-muted-foreground max-w-[220px] truncate" title={a.user_agent ?? ""}>
                    {a.user_agent ?? "—"}
                  </td>
                  <td className="p-3">
                    {a.reviewed_at ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Reviewed
                      </span>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => markReviewed(a.id)}>
                        Mark reviewed
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "critical" | "warning" | "info";
}) {
  const color =
    tone === "critical"
      ? "text-red-700"
      : tone === "warning"
      ? "text-amber-700"
      : tone === "info"
      ? "text-sky-700"
      : "text-navy";
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-soft">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 font-display text-2xl ${color}`}>{value}</div>
    </div>
  );
}

function SeverityBadge({ severity }: { severity: Alert["severity"] }) {
  const map = {
    critical: { cls: "bg-red-100 text-red-800", Icon: AlertTriangle, label: "Critical" },
    warning: { cls: "bg-amber-100 text-amber-800", Icon: AlertTriangle, label: "Warning" },
    info: { cls: "bg-sky-100 text-sky-800", Icon: Info, label: "Info" },
  }[severity];
  const Icon = map.Icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${map.cls}`}>
      <Icon className="h-3 w-3" /> {map.label}
    </span>
  );
}
